import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X, MapPin, Phone, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useLang } from "@/i18n";
import seoDict from "@/i18n/seo";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DiscoveryContext = createContext(() => {});
export const useDiscovery = () => useContext(DiscoveryContext);

const HEADSHOT =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_557b3c76-e584-41ea-851c-f19aad9c9b89/artifacts/mo6rbe4a_alessia_foto_chisono.jpg";
export { HEADSHOT };

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center border border-navy-900/15 text-[11px] font-medium tracking-widest" data-testid="lang-switcher-toggle">
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-option-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 uppercase transition-colors duration-300 ${
            lang === l ? "bg-navy-900 text-cream" : "text-navy-900/60 hover:text-navy-900"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const Nav = ({ onOpenDiscovery }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, id: "home" },
    { to: "/chi-sono", label: t.nav.about, id: "about" },
    { to: "/approccio", label: t.nav.approach, id: "approach" },
    { to: "/aree-competenza", label: t.nav.expertise, id: "expertise" },
    { to: "/insights", label: t.nav.insights, id: "insights" },
    { to: "/risorse", label: t.nav.resources, id: "resources" },
    { to: "/contatti", label: t.nav.contact, id: "contact" },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-navy-900/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Link to="/" className="group flex items-center gap-3" data-testid="nav-brand-link">
          <LogoMark theme="light" size={30} data-testid="logo-mark-header" />
          <span>
            <span className="block font-serif text-lg tracking-tight text-navy-900">{t.brand}</span>
            <span className="mt-0.5 block text-[9px] uppercase tracking-[0.3em] text-navy-900/50 group-hover:text-gold-deep transition-colors">
              {t.brandSub}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-5 lg:flex" data-testid="desktop-nav">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `link-editorial whitespace-nowrap text-[13px] tracking-wide transition-colors ${
                  isActive ? "text-navy-900 font-medium" : "text-navy-900/55 hover:text-navy-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LangToggle />
          <button
            onClick={onOpenDiscovery}
            data-testid="nav-discovery-call-btn"
            className="group flex items-center gap-2 bg-navy-900 px-5 py-2.5 text-[12px] font-medium tracking-wide text-cream transition-colors duration-300 hover:bg-sage-700"
          >
            {t.nav.cta}
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangToggle />
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? t.nav.close : t.nav.menu}
            className="p-2 text-navy-900"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-navy-900/10 bg-cream/95 backdrop-blur-xl lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {links.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className={({ isActive }) =>
                      `block py-2.5 font-serif text-2xl ${isActive ? "text-sage-700" : "text-navy-900"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <button
                onClick={() => { setOpen(false); onOpenDiscovery(); }}
                data-testid="mobile-discovery-call-btn"
                className="mt-4 bg-navy-900 px-5 py-3 text-sm font-medium text-cream"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

const DiscoveryModal = ({ open, onClose }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, source: "discovery-call" });
      toast.success(t.modal.success);
      setForm({ name: "", company: "", email: "", message: "" });
      onClose();
    } catch {
      toast.error(t.modal.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="discovery-call-modal"
        >
          <div className="absolute inset-0 bg-navy-950/60 backdrop-blur-sm" onClick={onClose} data-testid="discovery-modal-backdrop" />
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative m-0 w-full max-w-lg bg-cream p-8 shadow-2xl sm:m-6 sm:p-10"
          >
            <button onClick={onClose} data-testid="discovery-modal-close-btn" aria-label={t.modal.close} className="absolute right-5 top-5 text-navy-900/50 hover:text-navy-900">
              <X size={20} />
            </button>
            <p className="text-xs uppercase tracking-[0.28em] text-gold-deep">Discovery Call</p>
            <h3 className="mt-3 font-serif text-2xl text-navy-900 sm:text-3xl">{t.modal.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-navy-900/65">{t.modal.subtitle}</p>
            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="discovery-call-form">
              <input
                required
                data-testid="discovery-form-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.contact.form.namePh}
                className="w-full border border-navy-900/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-gold"
              />
              <input
                data-testid="discovery-form-company-input"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder={t.contact.form.companyPh}
                className="w-full border border-navy-900/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-gold"
              />
              <input
                required
                type="email"
                data-testid="discovery-form-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.contact.form.emailPh}
                className="w-full border border-navy-900/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-gold"
              />
              <textarea
                required
                rows={3}
                data-testid="discovery-form-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t.contact.form.messagePh}
                className="w-full resize-none border border-navy-900/15 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-gold"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="discovery-form-submit-btn"
                className="w-full bg-navy-900 py-3.5 text-sm font-medium tracking-wide text-cream transition-colors duration-300 hover:bg-sage-700 disabled:opacity-60"
              >
                {sending ? t.contact.form.sending : t.contact.form.submit}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Footer = ({ onOpenDiscovery }) => {
  const { t } = useLang();
  const links = [
    { to: "/", label: t.nav.home },
    { to: "/chi-sono", label: t.nav.about },
    { to: "/approccio", label: t.nav.approach },
    { to: "/aree-competenza", label: t.nav.expertise },
    { to: "/insights", label: t.nav.insights },
    { to: "/risorse", label: t.nav.resources },
    { to: "/contatti", label: t.nav.contact },
  ];
  return (
    <footer className="relative overflow-hidden bg-navy-900 text-cream grain" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="flex flex-col items-start justify-between gap-8 border-b border-cream/10 pb-12 lg:flex-row lg:items-center">
          <h2 className="max-w-xl font-serif text-3xl leading-snug sm:text-4xl" data-testid="footer-cta-title">
            {t.footer.ctaTitle}
          </h2>
          <button
            onClick={onOpenDiscovery}
            data-testid="footer-discovery-call-btn"
            className="group flex items-center gap-2 border border-gold/50 px-7 py-3.5 text-sm font-medium tracking-wide text-gold-light transition-all duration-300 hover:bg-gold hover:text-navy-900"
          >
            {t.footer.cta}
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <div className="grid gap-10 py-12 sm:grid-cols-3">
          <div>
            <p className="flex items-center gap-3 font-serif text-xl">
              <LogoMark theme="dark" size={28} data-testid="logo-mark-footer" />
              {t.brand}
            </p>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-cream/55">{t.footer.claim}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-gold">{t.footer.navTitle}</p>
            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2">
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="text-sm text-cream/60 transition-colors hover:text-gold-light">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-gold">{t.footer.contactTitle}</p>
            <div className="mt-4 space-y-2.5 text-sm text-cream/60">
              <p className="flex items-center gap-2.5"><MapPin size={14} className="text-gold" /> {t.footer.location}</p>
              <a href="tel:+393409726465" className="flex items-center gap-2.5 transition-colors hover:text-gold-light" data-testid="footer-phone-link">
                <Phone size={14} className="text-gold" /> +39 340 972 6465
              </a>
              <a href="mailto:info@eclenia.com" className="flex items-center gap-2.5 transition-colors hover:text-gold-light" data-testid="footer-email-link">
                <Mail size={14} className="text-gold" /> info@eclenia.com
              </a>
            </div>
          </div>
        </div>

        <div className="hairline-gold" />
        <p className="pt-6 text-xs text-cream/40">{t.footer.rights}</p>
      </div>
    </footer>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const seoMap = {
  "/": "home",
  "/chi-sono": "about",
  "/approccio": "approach",
  "/aree-competenza": "expertise",
  "/insights": "insights",
  "/insights/business-case-benessere-organizzativo": "article-business-case",
  "/insights/womens-health-at-work": "article-womens-health",
  "/insights/rischio-psicosociale-compliance-strategia": "article-rischio-psicosociale",
  "/risorse": "resources",
  "/contatti": "contact",
};

const SeoManager = () => {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const seo = seoDict[lang][seoMap[pathname] || "home"];
  useEffect(() => {
    document.title = seo.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", seo.description);
    let kw = document.querySelector('meta[name="keywords"]');
    if (seo.keywords) {
      if (!kw) {
        kw = document.createElement("meta");
        kw.name = "keywords";
        document.head.appendChild(kw);
      }
      kw.setAttribute("content", seo.keywords);
    }
  }, [seo]);
  return null;
};

export default function Layout({ children }) {
  const [modalOpen, setModalOpen] = useState(false);
  const open = () => setModalOpen(true);
  const { pathname } = useLocation();
  const isPrivate = pathname.startsWith("/admin") || pathname.startsWith("/q/");
  return (
    <DiscoveryContext.Provider value={open}>
      <ScrollToTop />
      <SeoManager />
      {!isPrivate && <Nav onOpenDiscovery={open} />}
      <main>{children}</main>
      {!isPrivate && <Footer onOpenDiscovery={open} />}
      <DiscoveryModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <Toaster position="bottom-right" />
    </DiscoveryContext.Provider>
  );
}
